/**
 * Drop an image anywhere on the Browse search row to search with it.
 *
 * The drop target is the whole `#front-search-element` row — the field, the button and the
 * padding around them — not just the button, because "drag it onto the search box" is what
 * someone tries first. The overlay it reveals is server-rendered inside the row
 * (`Front.tsx`); this only toggles the class and runs the search.
 *
 * Drag events fire per element, so moving the pointer between the input and the button
 * raises `dragleave` on the one being left. A depth counter, not a leave-means-gone rule, is
 * what keeps the overlay from flickering across those boundaries.
 */
import { getDomElement } from "../../utils/dom";
import { runImageSearch } from "./run";

const ACTIVE = "front-search-dragover";
const BUSY = "front-search-dropping";

/** A drag carrying files, as opposed to text selected on the page or a dragged link. */
function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}

export function initSearchImageDropzone(): void {
  const row = getDomElement("front-search-element");
  if (!row?.querySelector(".front-search-dropzone-overlay")) return;

  let depth = 0;
  const clear = () => {
    depth = 0;
    row.classList.remove(ACTIVE);
  };

  row.addEventListener("dragenter", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth++;
    row.classList.add(ACTIVE);
  });

  // Without preventDefault on dragover the browser refuses the drop and navigates to the
  // file instead — the one listener this cannot do without.
  row.addEventListener("dragover", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  });

  row.addEventListener("dragleave", (e) => {
    if (!hasFiles(e)) return;
    depth--;
    if (depth <= 0) clear();
  });

  row.addEventListener("drop", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    clear();
    const file = e.dataTransfer?.files[0];
    if (!file) return;
    // The overlay stays up while the image is read and embedded: it is the only feedback
    // for a drop, where the button has its own "Reading…" label.
    row.classList.add(ACTIVE, BUSY);
    void runImageSearch(file).finally(() => {
      row.classList.remove(ACTIVE, BUSY);
    });
  });
}
